import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useRoutingStore } from '@/stores/routingStore';
import { useUIStore } from '@/stores/uiStore';
import { useSearchStore } from '@/stores/searchStore';
import { searchService } from '@/services/searchService';
import type { Coordinates, SearchResult, Address, PlaceCategory } from '@/types';

interface MapContextMenuProps {
  x: number;
  y: number;
  lat: number;
  lng: number;
  onClose: () => void;
}

/** Google-style right-click menu: Directions from/to here + What's here? */
export function MapContextMenu({ x, y, lat, lng, onClose }: MapContextMenuProps) {
  const setWaypoint = useRoutingStore((s) => s.setWaypoint);
  const setActiveWaypointId = useRoutingStore((s) => s.setActiveWaypointId);
  const openPanel = useUIStore((s) => s.openPanel);
  const { setSelectedResult, setSelectedPlace } = useSearchStore();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const coords: Coordinates = { lat, lng };
  const coordLabel = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

  const setRouteEnd = (id: 'origin' | 'destination') => {
    setActiveWaypointId(null);
    setWaypoint(id, coords, coordLabel);
    openPanel('directionsPanel');
    onClose();
    // Enrich the waypoint label with a real address.
    searchService
      .reverseGeocode(coords)
      .then((r) => {
        if (r?.address?.formatted) setWaypoint(id, coords, r.address.formatted);
      })
      .catch(() => {/* keep coordinate label */});
  };

  const whatsHere = () => {
    const base: SearchResult = {
      id: `poi/${coordLabel}`,
      name: coordLabel,
      displayName: coordLabel,
      category: 'other' as PlaceCategory,
      coordinates: coords,
      address: { formatted: coordLabel } as Address,
      relevanceScore: 1,
    };
    setSelectedPlace(null);
    setSelectedResult(base);
    openPanel('placeDetailsPanel');
    onClose();
    searchService
      .reverseGeocode(coords)
      .then((r) => {
        const cur = useSearchStore.getState().selectedResult;
        if (r?.address && cur && cur.id === base.id) {
          const label = r.address.formatted || coordLabel;
          setSelectedResult({ ...base, address: r.address, name: label, displayName: label });
        }
      })
      .catch(() => {/* keep coordinates */});
  };

  // Keep the menu on-screen near the right/bottom edges.
  const style: React.CSSProperties = {
    left: Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 230),
    top: Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : 9999) - 180),
  };

  // Rendered via a portal to <body> so it stacks above the map AND all panels
  // (escaping the map's z-map stacking context). The full-screen map means
  // e.point pixel coords equal viewport coords, so `fixed` positioning is correct.
  return createPortal(
    <>
      {/* Backdrop catches outside-clicks / a second right-click */}
      <div
        className="fixed inset-0 z-[1000]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        className="fixed z-[1001] min-w-[208px] overflow-hidden rounded-lg bg-white py-1 text-sm shadow-dropdown"
        style={style}
      >
        <MenuItem icon="🟢" label="Directions from here" onClick={() => setRouteEnd('origin')} />
        <MenuItem icon="📍" label="Directions to here" onClick={() => setRouteEnd('destination')} />
        <div className="my-1 border-t border-gray-100" />
        <MenuItem icon="❓" label="What's here?" onClick={whatsHere} />
        <div className="px-3 pt-1 text-xs text-gray-400">{coordLabel}</div>
      </div>
    </>,
    document.body
  );
}

function MenuItem({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-gray-700 hover:bg-gray-50"
    >
      <span aria-hidden className="text-base leading-none">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
